import classNames from 'classnames';
import { PulseCircleAnimationMode } from 'enums';
import { motion } from 'framer-motion';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import * as React from 'react';
import { Trans } from 'react-i18next';
import { Text, Box } from '@wix/design-system';
import s from './AppTitle.scss';
import { SecondUpgradeSubtitle } from './SecondUpgradeSubtitle';
import { CirclePulseAnimation } from '../CirclePulseAnimation/CirclePulseAnimation';
import { useAppTitles } from './useAppTitles';

const variants = {
  fadeIn: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: {
      duration: 0.68,
      ease: [0.075, 0.82, 0.165, 1],
      delay: 0.5,
    },
  },

  exit: {
    opacity: 0,
    x: 0,
    y: 20,
    transition: {
      duration: 0.68,
      ease: [0.075, 0.82, 0.165, 1],
    },
  },
};

export interface AppSubtitleProps {
  rootStore: RootStore;
}

const AppSubtitleComp: React.FC<AppSubtitleProps> = ({ rootStore }) => {
  const {
    isSecondUpgrade,
    isChangePlan,
    isEditorXSpecialDiscountEnabled,
    isInSale,
    isFranchPackagePicker,
    isJapanesePackagePicker,
    isDayful,
  } = rootStore.appStore;

  const { appSubtitle } = useAppTitles(rootStore.appStore);
  const { theme } = usePageModeContext();

  const getCirclePulseAnimationMode = () => {
    if (isDayful) {
      return PulseCircleAnimationMode.DAYFUL;
    }
    if (isInSale) {
      return PulseCircleAnimationMode.SALE;
    }
    return PulseCircleAnimationMode.DEFAULT;
  };
  const circlePulseAnimationMode = getCirclePulseAnimationMode();

  const showPulse =
    isFranchPackagePicker || isJapanesePackagePicker || isDayful;
  return (
    <Text dataHook='page-sub-title' className={classNames(s.subtitle, theme)}>
      {isSecondUpgrade && !isEditorXSpecialDiscountEnabled ? (
        <SecondUpgradeSubtitle />
      ) : isChangePlan ? (
        <Trans i18nKey='change_plan.app_sub_title'>
          Compare plans and choose the one that works best for you
        </Trans>
      ) : (
        <>
          {showPulse && (
            <Box marginRight='10px' marginTop='2px'>
              <CirclePulseAnimation mode={circlePulseAnimationMode} />
            </Box>
          )}
          <Trans i18nKey={appSubtitle}>
            <span className={classNames('colors', s.bold, theme)}></span>
            {appSubtitle}
          </Trans>
        </>
      )}
    </Text>
  );
};

export const AppSubtitleContent = withInjectedRootStore(AppSubtitleComp);

export const AppSubtitleAnimated = () => (
  <motion.div variants={variants} initial='exit' animate='fadeIn'>
    <AppSubtitleContent />
  </motion.div>
);
